<script setup>
import { ref, reactive, computed, watch, onMounted, provide } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useChallengeCreationFlow } from '/app/composables/challenge-create-flow';
import { usePlatform } from '/app/composables/platform';
import { useFormContext } from '/app/composables/form';
import { bus } from '/app/plugins/bus';
import ChallengeDrafts from './Drafts.vue';
import NIcon from '/app/components/NIcon.vue';
import { vElementVisibility as vVisible } from '@vueuse/components';

const emit = defineEmits(['change']);

const form = useFormContext();
const route = useRoute();
const router = useRouter();

const { isValid } = useChallengeCreationFlow();

const stepperEl = ref(null);
const drafts = ref(null);

const steps = reactive([]);
const selected = ref(null);
const animated = ref(false);

const visibles = computed(() => steps.filter((step) => !step.hidden));
const activeIndex = computed(() => visibles.value.findIndex((step) => step.slug === selected.value) || 0);
const prevTitle = computed(() => activeIndex.value > 0 ? visibles.value[activeIndex.value - 1].title : null);
const nextTitle = computed(() => activeIndex.value === visibles.value.length - 1 ? null : visibles.value[activeIndex.value + 1].title);
const isFirst = computed(() => visibles.value.length > 0 && activeIndex.value === 0);
const isLast = computed(() => visibles.value.length > 0 && activeIndex.value === visibles.value.length - 1);
const pixelWidth = computed(() => visibles.value.length * 120);
const prevStep = computed(() => activeIndex.value > 0 ? visibles.value[activeIndex.value - 1] : {});
const nextStep = computed(() => activeIndex.value === visibles.value.length - 1 ? {} : visibles.value[activeIndex.value + 1]);
const activeStep = computed(() => visibles.value[activeIndex.value] || {});

watch(selected, () => {
	emit('change');
});

function scrollToSteps() {
	stepperEl.value?.scrollIntoView({
		block: 'start',
		behavior: 'smooth',
	});
}

function to(slug) {
	selected.value = slug;
	bus('challenge:creation-flow:stepper:changed', { active: slug });
	scrollToSteps();
}

function number(n = 1) {
	const step = visibles.value[n - 1];
	if (step && !step.disabled) {
		to(step.slug);
	}
}

function prev() {
	const step = visibles.value[activeIndex.value - 1];
	if (step && !step.disabled) {
		to(step.slug);
	}
}

function next() {
	const step = visibles.value[activeIndex.value + 1];
	if (step && !step.disabled) {
		to(step.slug);
	}
}

function has(slug) {
	return steps.some((step) => step.slug === slug);
}

function submit() {
	form.submit();
}

function reorder({ slug, order, select = false }) {
	const step = steps.find((step) => step.slug === slug);
	if (!step) return;

	const index = steps.indexOf(step);
	if (index > -1 && index !== order) {
		steps.splice(index, 1);
		steps.splice(order, 0, step);
	}

	if (select) {
		setTimeout(() => {
			animated.value = false;
			to(slug);
		}, 1000);
	}
}

function whenVisible(visible) {
	visible && bus('challenge:stepper:visible');
}

function startOver() {
	bus('challenge:creation-flow:start-over');
	to(steps[0].slug);
}

const stepper = reactive({
	steps,
	selected,
	animated,
	visibles,
	activeIndex,
	prevTitle,
	nextTitle,
	isFirst,
	isLast,
	pixelWidth,
	prevStep,
	nextStep,
	activeStep,
	number,
	prev,
	next,
	to,
	has,
	submit,
	reorder,
	scrollToSteps,
	whenVisible,
	startOver,
});

provide('stepper', stepper);

defineExpose(stepper);

onMounted(() => {
	selected.value = steps[0].slug;

	if (route.query.start) {
		switch (route.query.start) {
			case 'promotions':
				drafts.value.open({
					title: 'Saved QQs to promote',
					origin: 'challenge-promotion-flow',
				});
				break;
		}

		router.replace({
			query: { start: null },
		});
	}
});
</script>

<template>
	<div
		ref="stepperEl"
		class="py-5 -my-5"
	>
		<div
			class="flex flex-wrap gap-2 justify-center py-4"
			v-visible="whenVisible"
		>
			<button aria-label="button"
				v-show="form.dirty && !isLast"
				type="button"
				class="rounded-md bg-secondary-500 text-white focus:bg-secondary-600 hover:bg-secondary-600 px-4 py-2 leading-tight"
				@click="startOver"
			>
				Start over
			</button>
			<button aria-label="button"
				v-show="form.dirty && !isLast"
				type="button"
				class="rounded-md bg-secondary-500 text-white focus:bg-secondary-600 hover:bg-secondary-600 px-4 py-2 leading-tight"
				@click="next"
			>
				Resume prior question
			</button>

			<ChallengeDrafts
				ref="drafts"
				#default="{ open, groups }"
			>
				<button aria-label="button"
					v-if="!isLast"
					type="button"
					class="rounded-md bg-secondary-500 text-white focus:bg-secondary-600 hover:bg-secondary-600 px-4 py-2 leading-tight"
					@click="open({ origin: 'challenge-promotion-flow', title: 'Saved QQs to promote' })"
				>
					Saved QQs to promote
					<span
						v-if="groups['challenge-promotion-flow']"
						class="ml-1"
						>({{ groups['challenge-promotion-flow'].length }})</span
					>
				</button>

				<button aria-label="button"
					v-if="!isLast"
					type="button"
					class="rounded-md bg-secondary-500 text-white focus:bg-secondary-600 hover:bg-secondary-600 px-4 py-2 leading-tight"
					@click="open({ origin: 'challenge-creation-flow', title: 'Saved drafts' })"
				>
					Saved drafts
					<span
						v-if="groups['challenge-creation-flow']"
						class="ml-1"
						>({{ groups['challenge-creation-flow'].length }})</span
					>
				</button>
			</ChallengeDrafts>
		</div>
		<div v-show="selected !== 'category' && !isLast">
			<div class="relative overflow-auto mb-6 pt-1">
				<div class="z-10 w-10 absolute left-0 top-0 bottom-2 bg-gradient-to-r from-white"></div>
				<div class="z-10 w-10 absolute right-0 top-0 bottom-2 bg-gradient-to-l from-white"></div>
				<div
					ref="breadcrumbs"
					class="challenge-steps overflow-y-hidden snap snap-x !m-0"
				>
					<TransitionGroup
						:move-class="animated ? 'breadcrumb-move' : null"
						tag="div"
						class="challenge-steps__inside"
						:style="{ width: pixelWidth + 'px' }"
						appear
					>
						<button aria-label="button"
							v-for="(step, index) in visibles"
							class="challenge-step snap-proximity"
							type="button"
							:class="{ 'is-active': step.isOpen }"
							:key="step.slug"
							:id="`step-${step.slug}`"
							:disabled="step.disabled"
							@click="to(step.slug)"
						>
							<span
								class="challenge-step__num"
								:class="{ 'is-complete': step.complete, 'ring-offset-1 ring-4 ring-secondary-300': step.isOpen }"
							>
								<NIcon
									v-if="step.complete"
									as="check-solid"
									style="padding-top: 1px"
								/>

								<template v-else>{{ index + 1 }}</template>
							</span>
							<span class="challenge-step__title bg-white px-2 py-1 -mx-2">{{ step.title }} </span>
						</button>
					</TransitionGroup>
				</div>
			</div>
		</div>
		<div class="grid grid-cols-12 gap-4">
			<slot
				v-if="activeStep.showLeft"
				name="left"
				:selected="selected"
			/>
			<slot />
			<slot
				v-if="activeStep.showRight"
				name="right"
				:selected="selected"
			/>
		</div>
		<div
			v-show="!activeStep.hideActions"
			class="stepper-footer"
		>
			<div class="grid grid-cols-12 gap-4">
				<div class="col-span-12 lg:col-span-6 stepper-footer__col is-left">
					<span
						v-if="prevTitle"
						class="stepper-footer__help-text flex items-center"
					>
						<span
							aria-hidden="true"
							class="icon icon-chevron-left"
						></span>
						{{ prevTitle }}
					</span>
					<button aria-label="button"
						class="btn is-orange"
						:disabled="!prevTitle || prevStep.disabled"
						@click.prevent="prev"
					>
						Back
					</button>
				</div>
				<div class="col-span-12 lg:col-span-6 stepper-footer__col is-right">
					<button aria-label="button"
						v-if="activeStep.activator"
						type="button"
						class="btn is-orange"
						:disabled="form.busy || !isValid"
						@click="submit"
					>
						{{ form.busy ? 'Saving...' : 'Save' }}
					</button>
					<button aria-label="button"
						v-else-if="nextTitle"
						:disabled="nextStep.disabled"
						type="button"
						class="btn is-orange"
						@click.prevent="next"
					>
						Next
					</button>
					<span
						v-if="nextTitle"
						class="stepper-footer__help-text flex items-center"
					>
						{{ nextTitle }}
						<span
							aria-hidden="true"
							class="icon icon-chevron-right"
						></span>
					</span>
				</div>
				<!-- <div class="col-span-12 lg:col-span-6 stepper-footer__col is-draft">
                    <button aria-label="button" class="btn is-draft">Save Draft</button>
                </div> -->
			</div>
		</div>
	</div>
</template>

<style lang="scss">
.breadcrumb-move {
	@apply transform ease-in-out duration-1000;
}
</style>
